/**
 * Utilitaire pour ajouter le logo Keslakay aux PDF
 */

let cachedKeslakayLogo = null

/**
 * Charge le logo Keslakay depuis les assets
 * @returns {Promise<string|null>} Le logo en base64 ou null
 */
export async function loadKeslakayLogo() {
  // Si déjà en cache, retourner directement
  if (cachedKeslakayLogo) {
    return cachedKeslakayLogo
  }

  try {
    // Charger le logo Keslakay depuis les assets
    const logoUrl = require('@/assets/img/logo.png')
    
    // Convertir en base64
    return new Promise((resolve) => {
      const img = new Image()
      img.crossOrigin = 'Anonymous'
      img.onload = () => {
        const canvas = document.createElement('canvas')
        canvas.width = img.width
        canvas.height = img.height
        const ctx = canvas.getContext('2d')
        ctx.drawImage(img, 0, 0)
        const dataURL = canvas.toDataURL('image/png')
        cachedKeslakayLogo = dataURL
        resolve(dataURL)
      }
      img.onerror = () => {
        console.error('Erreur lors du chargement du logo Keslakay')
        resolve(null)
      }
      img.src = logoUrl
    })
  } catch (error) {
    console.error('Erreur lors du chargement du logo Keslakay:', error)
    return null
  }
}

/**
 * Ajoute le logo Keslakay et un footer à toutes les pages du PDF
 * @param {jsPDF} doc - L'instance jsPDF
 */
export async function addKeslakayBranding(doc) {
  try {
    const logo = await loadKeslakayLogo()
    const pageCount = doc.internal.getNumberOfPages()
    const pageHeight = doc.internal.pageSize.getHeight()
    const pageWidth = doc.internal.pageSize.getWidth()
    
    // Get store information from localStorage
    let storeName = ''
    let storePhone = ''
    try {
      const business = JSON.parse(localStorage.getItem('business') || '{}')
      storeName = business.name || ''
      storePhone = business.phone || ''
    } catch (error) {
      console.error('Error loading business info:', error)
    }
    
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i)
      
      // ========== EN-TÊTE (HEADER) ==========
      // Logo Keslakay dans le header (à gauche)
      if (logo) {
        try {
          // Position et taille du logo
          const logoWidth = 80   // Largeur du logo
          const logoHeight = 20  // Hauteur du logo
          const logoX = 40       // Position X
          const logoY = 10       // Position Y
          
          // Ajouter le logo
          doc.addImage(logo, 'PNG', logoX, logoY, logoWidth, logoHeight, undefined, 'FAST')
        } catch (error) {
          console.error('Erreur lors de l\'ajout du logo Keslakay au header:', error)
        }
      }
      
      // Store information (à droite, opposite to logo)
      if (storeName || storePhone) {
        doc.setFontSize(11)
        doc.setFont('helvetica', 'bold')
        doc.setTextColor(0, 0, 0)
        
        // Position on the right side
        const rightX = pageWidth - 40 // 20mm from right edge
        const yPos = 25
        
        // Combine store name and phone on same line
        let storeInfo = ''
        if (storeName && storePhone) {
          storeInfo = `${storeName} - ${storePhone}`
        } else if (storeName) {
          storeInfo = storeName
        } else if (storePhone) {
          storeInfo = storePhone
        }
        
        if (storeInfo) {
          doc.text(storeInfo, rightX, yPos, { align: 'right' })
        }
      }
      
      // ========== PIED DE PAGE (FOOTER) ==========
      // Ligne de séparation au-dessus du footer
      doc.setDrawColor(220, 220, 220)
      doc.setLineWidth(0.3)
      doc.line(20, pageHeight - 30, pageWidth - 20, pageHeight - 30)
      
      // Logo Keslakay dans le footer (à gauche)
      if (logo) {
        try {
          // Logo simplifié dans le footer - taille uniforme
          const footerLogoSize = 14 // Taille carrée pour éviter distorsion
          doc.addImage(logo, 'PNG', 20, pageHeight - 28, footerLogoSize, footerLogoSize, undefined, 'FAST')
        } catch (error) {
          console.error('Erreur lors de l\'ajout du logo Keslakay au footer:', error)
        }
      }
      
      // Texte "Powered by Keslakay" à côté du logo
      doc.setFontSize(8)
      doc.setTextColor(100, 100, 100)
      doc.setFont('helvetica', 'italic')
      doc.text('Powered by', 37, pageHeight - 23)
      doc.setFont('helvetica', 'bold')
      doc.setTextColor(0, 123, 255)
      doc.text('Keslakay', 37, pageHeight - 18)
      
      // Informations de pagination (centre)
      doc.setFont('helvetica', 'normal')
      doc.setFontSize(8)
      doc.setTextColor(128, 128, 128)
      doc.text(`Page ${i} sur ${pageCount}`, pageWidth / 2, pageHeight - 22, { align: 'center' })
      
      // Date de génération (à droite)
      const now = new Date()
      const dateStr = now.toLocaleDateString('fr-FR')
      const timeStr = now.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })
      doc.setFontSize(7)
      doc.text(`Généré le ${dateStr}`, pageWidth - 20, pageHeight - 24, { align: 'right' })
      doc.text(`à ${timeStr}`, pageWidth - 20, pageHeight - 20, { align: 'right' })
    }
    
    return doc
  } catch (error) {
    console.error('Erreur lors de l\'ajout du branding Keslakay:', error)
    return doc
  }
}

/**
 * Fonction helper pour exporter un PDF avec le branding Keslakay
 * @param {jsPDF} doc - L'instance jsPDF
 * @param {string} filename - Nom du fichier
 * @param {Object} _options - Options (réservé pour usage futur)
 */
// eslint-disable-next-line no-unused-vars
export async function savePDFWithBranding(doc, filename, _options = {}) {
  await addKeslakayBranding(doc)
  doc.save(filename)
}
